<template>
  <div class="space-y-6">
    <!-- Tab Toggle: Approval vs Endorsement -->
    <div class="flex flex-wrap items-center justify-between gap-3">
      <div class="flex gap-2">
        <Button
          :label="`Pending Approval${approvalCount ? ` (${approvalCount})` : ''}`"
          :severity="activeView === 'approval' ? undefined : 'secondary'"
          :outlined="activeView !== 'approval'"
          size="small"
          icon="pi pi-check-circle"
          @click="activeView = 'approval'"
        />
        <Button
          :label="`Pending Endorsement${endorsementCount ? ` (${endorsementCount})` : ''}`"
          :severity="activeView === 'endorsement' ? undefined : 'secondary'"
          :outlined="activeView !== 'endorsement'"
          size="small"
          icon="pi pi-verified"
          @click="activeView = 'endorsement'"
        />
      </div>

      <!-- Pending-approval notification opt-in -->
      <div
        v-if="!subscriptionLoading"
        class="flex items-center gap-2"
        data-testid="approval-notify-control"
      >
        <i class="pi pi-bell text-gray-500 text-sm"></i>
        <span class="text-sm text-gray-600">Notify me of new pending approvals</span>
        <InputSwitch
          :modelValue="subscribed"
          :disabled="subscriptionSaving"
          data-testid="approval-notify-toggle"
          @update:modelValue="handleSubscriptionToggle"
        />
      </div>
    </div>

    <!-- Filters -->
    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
      <div class="sm:col-span-2 xl:col-span-2">
        <label class="block text-[11px] font-medium text-gray-500 mb-1">Search</label>
        <InputText
          v-model="searchTerm"
          size="small"
          placeholder="Matric number or name..."
          class="w-full rounded-lg"
          @keyup.enter="handleSearch"
        />
      </div>
      <div>
        <label class="block text-[11px] font-medium text-gray-500 mb-1">Level</label>
        <Select
          v-model="selectedLevel"
          :options="levelOptions"
          optionLabel="label"
          optionValue="value"
          placeholder="All Levels"
          size="small"
          showClear
          class="w-full rounded-lg"
        />
      </div>
      <div class="flex items-end">
        <Button
          label="Filter"
          icon="pi pi-filter"
          size="small"
          severity="secondary"
          class="w-full"
          @click="handleSearch"
        />
      </div>
    </div>

    <!-- Apply to All Filtered -->
    <div
      v-if="!loading && registrations.length"
      class="flex flex-wrap items-center justify-end gap-2"
      data-testid="apply-all-filtered-bar"
    >
      <span class="text-xs text-gray-500">Apply to all filtered:</span>
      <Button
        v-if="activeView === 'approval' && canApprove"
        label="Approve All"
        icon="pi pi-check"
        size="small"
        outlined
        severity="success"
        :disabled="!scopeAvailable"
        v-tooltip.top="scopeAvailable ? 'Approve every pending registration matching the current filters' : 'Set a level filter first'"
        @click="openBulkDialog(ApprovalActionEnum.Approve, 'scope')"
      />
      <Button
        v-if="activeView === 'endorsement' && canEndorse"
        label="Endorse All"
        icon="pi pi-verified"
        size="small"
        outlined
        severity="success"
        :disabled="!scopeAvailable"
        v-tooltip.top="scopeAvailable ? 'Endorse every adviser-approved registration matching the current filters' : 'Set a level filter first'"
        @click="openBulkDialog(ApprovalActionEnum.Endorse, 'scope')"
      />
      <Button
        v-if="canReject"
        label="Reject All"
        icon="pi pi-times"
        size="small"
        outlined
        severity="danger"
        :disabled="!scopeAvailable"
        v-tooltip.top="scopeAvailable ? 'Reject every registration matching the current filters' : 'Set a level filter first'"
        @click="openBulkDialog(ApprovalActionEnum.Reject, 'scope')"
      />
    </div>

    <!-- Bulk Action Bar (appears when rows are selected) -->
    <div
      v-if="selectedRows.length"
      class="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-primary/20 bg-primary/5 px-4 py-2"
      data-testid="bulk-action-bar"
    >
      <div class="flex items-center gap-2">
        <Badge :value="selectedRows.length" severity="info" />
        <span class="text-sm text-gray-700">selected</span>
        <Button label="Clear" size="small" text severity="secondary" @click="selectedRows = []" />
      </div>
      <div class="flex gap-2">
        <Button
          v-if="activeView === 'approval' && canApprove"
          label="Approve Selected"
          icon="pi pi-check"
          size="small"
          severity="success"
          @click="openBulkDialog(ApprovalActionEnum.Approve, 'selection')"
        />
        <Button
          v-if="activeView === 'endorsement' && canEndorse"
          label="Endorse Selected"
          icon="pi pi-verified"
          size="small"
          severity="success"
          @click="openBulkDialog(ApprovalActionEnum.Endorse, 'selection')"
        />
        <Button
          v-if="canReject"
          label="Reject Selected"
          icon="pi pi-times"
          size="small"
          severity="danger"
          @click="openBulkDialog(ApprovalActionEnum.Reject, 'selection')"
        />
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="flex items-center justify-center py-12">
      <span class="pi pi-spin pi-spinner text-3xl text-primary"></span>
    </div>

    <!-- Data Table -->
    <DataTable
      size="small"
      v-else-if="registrations.length"
      :value="registrations"
      v-model:selection="selectedRows"
      dataKey="student.id"
      responsiveLayout="scroll"
      class="p-datatable-sm"
    >
      <Column selectionMode="multiple" headerStyle="width: 3rem" />

      <Column header="Student" style="min-width: 200px">
        <template #body="{ data }">
          <div>
            <p class="font-semibold text-sm text-gray-800">
              {{ data.student.surname }} {{ data.student.first_name }}
            </p>
            <p class="text-xs text-gray-500">{{ data.student.matric_no }}</p>
          </div>
        </template>
      </Column>

      <Column header="Programme" style="min-width: 150px">
        <template #body="{ data }">
          <span class="text-sm">{{ data.programme }}</span>
        </template>
      </Column>

      <Column header="Level" style="width: 80px">
        <template #body="{ data }">
          <Badge :value="`${data.level}`" severity="info" />
        </template>
      </Column>

      <Column header="Courses" style="width: 90px">
        <template #body="{ data }">
          <span class="text-sm">{{ data.course_count }}</span>
        </template>
      </Column>

      <Column header="Units" style="width: 80px">
        <template #body="{ data }">
          <Badge :value="`${data.total_units}`" severity="secondary" />
        </template>
      </Column>

      <Column header="Session" style="min-width: 120px">
        <template #body="{ data }">
          <span class="text-xs text-gray-600">{{ data.session }} — {{ data.semester }}</span>
        </template>
      </Column>

      <Column v-if="activeView === 'endorsement'" header="Approved By" style="min-width: 120px">
        <template #body="{ data }">
          <div v-if="data.level_adviser">
            <p class="text-xs text-gray-700">{{ data.level_adviser.name }}</p>
            <p v-if="data.level_adviser_approved_at" class="text-[10px] text-gray-400">
              {{ formatDate(data.level_adviser_approved_at) }}
            </p>
          </div>
          <span v-else class="text-xs text-gray-400">—</span>
        </template>
      </Column>

      <Column header="Actions" style="width: 160px">
        <template #body="{ data }">
          <div class="flex gap-2">
            <Button
              icon="pi pi-eye"
              size="small"
              severity="info"
              text
              v-tooltip.top="'View Courses'"
              @click="openDetailDialog(data)"
            />
            <Button
              v-if="activeView === 'approval' && canApprove"
              icon="pi pi-check"
              size="small"
              severity="success"
              v-tooltip.top="'Approve'"
              @click="openActionDialog(data, 'approve')"
            />
            <Button
              v-if="activeView === 'endorsement' && canEndorse"
              icon="pi pi-verified"
              size="small"
              severity="success"
              v-tooltip.top="'Endorse'"
              @click="openActionDialog(data, 'endorse')"
            />
            <Button
              v-if="canReject"
              icon="pi pi-times"
              size="small"
              severity="danger"
              v-tooltip.top="'Reject'"
              @click="openRejectDialog(data)"
            />
          </div>
        </template>
      </Column>
    </DataTable>

    <!-- Pagination -->
    <div v-if="totalPages > 1" class="flex justify-center pt-2">
      <Paginator
        :rows="perPage"
        :totalRecords="totalRecords"
        :first="(currentPage - 1) * perPage"
        @page="onPageChange"
      />
    </div>

    <!-- Empty State -->
    <div v-if="!loading && !registrations.length" class="text-center py-12">
      <i class="pi pi-inbox text-4xl text-gray-400 mb-4"></i>
      <p class="text-gray-500 text-lg">
        {{ activeView === 'approval'
          ? 'No pending course registrations to approve.'
          : 'No course registrations pending endorsement.' }}
      </p>
    </div>

    <!-- Detail Dialog -->
    <Dialog
      v-model:visible="showDetailDialog"
      modal
      :closable="true"
      :dismissableMask="true"
      :style="{ width: '600px' }"
      :pt="{ root: { class: 'rounded-xl overflow-hidden' } }"
    >
      <template #header>
        <div class="flex items-center gap-3">
          <div class="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10">
            <i class="pi pi-list text-primary"></i>
          </div>
          <div v-if="selectedRegistration">
            <h3 class="font-semibold text-gray-800 text-base">
              {{ selectedRegistration.student.surname }} {{ selectedRegistration.student.first_name }}
            </h3>
            <p class="text-xs text-gray-500">
              {{ selectedRegistration.student.matric_no }} — {{ selectedRegistration.programme }} (Level {{ selectedRegistration.level }})
            </p>
          </div>
        </div>
      </template>

      <div v-if="selectedRegistration" class="space-y-4">
        <div class="flex items-center gap-4 text-sm text-gray-600">
          <span><strong>Session:</strong> {{ selectedRegistration.session }}</span>
          <span><strong>Semester:</strong> {{ selectedRegistration.semester }}</span>
          <span><strong>Total Units:</strong> {{ selectedRegistration.total_units }}</span>
        </div>

        <DataTable
          size="small"
          :value="selectedRegistrationCourses"
          responsiveLayout="scroll"
          class="p-datatable-sm"
        >
          <Column header="Code" style="width: 100px">
            <template #body="{ data }">
              <span class="font-mono text-xs">{{ data.course?.course_code }}</span>
            </template>
          </Column>
          <Column header="Title">
            <template #body="{ data }">
              <span class="text-sm">{{ data.course?.course_title }}</span>
            </template>
          </Column>
          <Column header="Units" style="width: 70px">
            <template #body="{ data }">
              {{ data.course?.unit }}
            </template>
          </Column>
          <Column header="Status" style="width: 100px">
            <template #body="{ data }">
              <Tag :value="courseStatusLabel(data.course_status)" :severity="courseStatusSeverity(data.course_status)" class="text-[10px]" />
            </template>
          </Column>
        </DataTable>
      </div>

      <template #footer>
        <div class="flex justify-end gap-2">
          <Button size="small" label="Close" severity="secondary" text @click="showDetailDialog = false" />
          <Button size="small"
            v-if="activeView === 'approval' && canApprove && selectedRegistration"
            label="Approve All"
            icon="pi pi-check"
            severity="success"
            @click="showDetailDialog = false; openActionDialog(selectedRegistration!, 'approve')"
          />
          <Button size="small"
            v-if="activeView === 'endorsement' && canEndorse && selectedRegistration"
            label="Endorse All"
            icon="pi pi-verified"
            severity="success"
            @click="showDetailDialog = false; openActionDialog(selectedRegistration!, 'endorse')"
          />
        </div>
      </template>
    </Dialog>

    <!-- Action Confirm Dialog (Approve / Endorse) -->
    <Dialog
      v-model:visible="showActionDialog"
      modal
      :closable="true"
      :dismissableMask="true"
      :style="{ width: '440px' }"
      :pt="{ root: { class: 'rounded-xl overflow-hidden' } }"
    >
      <template #header>
        <div class="flex items-center gap-3">
          <div class="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10">
            <i class="pi" :class="pendingAction === 'endorse' ? 'pi-verified' : 'pi-check-circle'" style="color: var(--green-600)"></i>
          </div>
          <div>
            <h3 class="font-semibold text-gray-800 text-base capitalize">{{ pendingAction }} Registration</h3>
            <p class="text-xs text-gray-500">This action applies to all registered courses</p>
          </div>
        </div>
      </template>

      <div v-if="actionTarget" class="py-2 space-y-2">
        <p class="text-sm text-gray-700">
          {{ pendingAction === 'approve' ? 'Approve' : 'Endorse' }} course registration for
          <strong>{{ actionTarget.student.surname }} {{ actionTarget.student.first_name }}</strong>
          ({{ actionTarget.course_count }} courses, {{ actionTarget.total_units }} units)?
        </p>
      </div>

      <template #footer>
        <div class="flex justify-end gap-2">
          <Button size="small" label="Cancel" severity="secondary" text @click="showActionDialog = false" />
          <Button size="small"
            :label="pendingAction === 'approve' ? 'Approve' : 'Endorse'"
            :icon="pendingAction === 'endorse' ? 'pi pi-verified' : 'pi pi-check'"
            severity="success"
            :loading="mutating"
            @click="handleAction"
          />
        </div>
      </template>
    </Dialog>

    <!-- Reject Dialog -->
    <Dialog
      v-model:visible="showRejectDialog"
      modal
      :closable="true"
      :dismissableMask="true"
      :style="{ width: '440px' }"
      :pt="{ root: { class: 'rounded-xl overflow-hidden' } }"
    >
      <template #header>
        <div class="flex items-center gap-3">
          <div class="flex items-center justify-center w-10 h-10 rounded-full bg-red-50">
            <i class="pi pi-times-circle text-red-600"></i>
          </div>
          <div>
            <h3 class="font-semibold text-gray-800 text-base">Reject Registration</h3>
            <p class="text-xs text-gray-500">Please provide a reason for rejection</p>
          </div>
        </div>
      </template>

      <div v-if="rejectTarget" class="space-y-4 py-2">
        <p class="text-sm text-gray-700">
          Reject course registration for
          <strong>{{ rejectTarget.student.surname }} {{ rejectTarget.student.first_name }}</strong>?
        </p>
        <div>
          <label class="block text-xs font-medium text-gray-600 mb-1">Reason *</label>
          <Textarea v-model="rejectReason" rows="3" class="w-full" placeholder="Enter reason for rejection..." />
        </div>
      </div>

      <template #footer>
        <div class="flex justify-end gap-2">
          <Button size="small" label="Cancel" severity="secondary" text @click="showRejectDialog = false" />
          <Button size="small"
            label="Reject"
            icon="pi pi-times"
            severity="danger"
            :loading="mutating"
            :disabled="!rejectReason.trim()"
            @click="handleReject"
          />
        </div>
      </template>
    </Dialog>

    <!-- Bulk Action Confirm Dialog -->
    <Dialog
      v-model:visible="showBulkDialog"
      modal
      :closable="true"
      :dismissableMask="true"
      :style="{ width: '460px' }"
      :pt="{ root: { class: 'rounded-xl overflow-hidden' } }"
    >
      <template #header>
        <div class="flex items-center gap-3">
          <div
            class="flex items-center justify-center w-10 h-10 rounded-full"
            :class="bulkAction === ApprovalActionEnum.Reject ? 'bg-red-50' : 'bg-green-50'"
          >
            <i
              class="pi"
              :class="bulkAction === ApprovalActionEnum.Reject ? 'pi-times-circle text-red-600' : 'pi-check-circle text-green-600'"
            ></i>
          </div>
          <div>
            <h3 class="font-semibold text-gray-800 text-base">{{ bulkActionLabel(bulkAction) }} Registrations</h3>
            <p class="text-xs text-gray-500">
              {{ bulkMode === 'selection' ? `${selectedRows.length} student(s) selected` : 'All registrations matching the current filters' }}
            </p>
          </div>
        </div>
      </template>

      <div class="space-y-4 py-2">
        <p class="text-sm text-gray-700">{{ bulkConfirmMessage }}</p>
        <div v-if="bulkAction === ApprovalActionEnum.Reject">
          <label class="block text-xs font-medium text-gray-600 mb-1">Reason *</label>
          <Textarea v-model="bulkReason" rows="3" class="w-full" placeholder="Enter reason for rejection..." />
        </div>
      </div>

      <template #footer>
        <div class="flex justify-end gap-2">
          <Button size="small" label="Cancel" severity="secondary" text @click="showBulkDialog = false" />
          <Button
            size="small"
            :label="bulkActionLabel(bulkAction)"
            :icon="bulkAction === ApprovalActionEnum.Reject ? 'pi pi-times' : 'pi pi-check'"
            :severity="bulkAction === ApprovalActionEnum.Reject ? 'danger' : 'success'"
            :loading="bulkMutating"
            :disabled="bulkAction === ApprovalActionEnum.Reject && !bulkReason.trim()"
            @click="handleBulkAction"
          />
        </div>
      </template>
    </Dialog>
  </div>
</template>

<script setup lang="ts">
import { useMutation } from '@vue/apollo-composable';
import { useToast } from 'primevue/usetoast';
import { useUserStore } from '@/store/userStore';
import { can, AppPermissions } from '@/utils/permissions';
import { usePendingCourseRegistrationsForApprovalQuery, usePendingCourseRegistrationsForEndorsementQuery, useApproveStudentCourseRegistrationMutation, useRejectStudentCourseRegistrationMutation, useEndorseCourseRegistrationByHodMutation } from '@/types/vue-apollo-helpers';
import { useConfirm } from 'primevue/useconfirm';
import { useApprovalNotificationSubscription } from '@/composables/useApprovalNotificationSubscription';
import { ApprovalActionEnum } from '@/types/composition-functions';
import { BULK_COURSE_REGISTRATION_ACTION } from '@/graphql/mutations/tertiary';
import { buildSelectionBulkInput, buildScopeBulkInput, hasScopeCriteria, bulkActionLabel, registrationIdsFromRows } from '@/utils/bulkRegistrationActions';
import type { BulkRegistrationActionResult } from '@/utils/bulkRegistrationActions';
import { useTertiaryLevels } from '@/composables/queries/useTertiaryLevels';

interface ApprovalRegistration {
  student: { id: string; surname: string; first_name: string; matric_no: string };
  programme: string;
  level: string;
  course_count: number;
  total_units: number;
  session: string;
  semester: string;
  academic_calendar: { id: string };
  registrations?: Array<{ coursesRegistered?: Array<{ course?: Record<string, unknown>; course_status?: string }> }>;
  level_adviser?: { name: string } | null;
  level_adviser_approved_at?: string | null;
}

const toast = useToast();
const userStore = useUserStore();
const currentUser = computed(() => userStore.currentUser);

// Permissions
const canApprove = computed(() => can(currentUser.value, AppPermissions.CourseRegistrationApproval.Actions.Approve, AppPermissions.CourseRegistrationApproval.Resource));
const canReject = computed(() => can(currentUser.value, AppPermissions.CourseRegistrationApproval.Actions.Reject, AppPermissions.CourseRegistrationApproval.Resource));
const canEndorse = computed(() => can(currentUser.value, AppPermissions.CourseRegistrationApproval.Actions.Endorse, AppPermissions.CourseRegistrationApproval.Resource));

// View toggle
const activeView = ref<'approval' | 'endorsement'>('approval');

// Pending-approval notification opt-in
const {
  subscribed,
  scopedRows: subscriptionScopedRows,
  loading: subscriptionLoading,
  saving: subscriptionSaving,
  setSubscribed,
} = useApprovalNotificationSubscription();
const confirm = useConfirm();

async function handleSubscriptionToggle(value: boolean) {
  // Switching off without a scope removes EVERY subscription row, including
  // department/programme scopes managed from My Account (#500): ask first.
  if (!value && subscriptionScopedRows.value.length) {
    confirm.require({
      header: 'Turn off all approval notifications?',
      message: `This also removes your ${subscriptionScopedRows.value.length} department/programme subscription(s). Manage individual scopes under My Account → Notifications.`,
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Turn off all',
      rejectLabel: 'Keep',
      acceptProps: { severity: 'danger' },
      accept: () => applySubscriptionToggle(false),
    });
    return;
  }
  await applySubscriptionToggle(value);
}

async function applySubscriptionToggle(value: boolean) {
  try {
    await setSubscribed(value);
    toast.add({
      severity: 'success',
      summary: value ? 'Subscribed' : 'Unsubscribed',
      detail: value
        ? 'You will be notified when new course registrations are pending approval.'
        : 'You will no longer receive pending-approval notifications.',
      life: 3000,
    });
  } catch (err: unknown) {
    const gqlErr = err as { graphQLErrors?: Array<{ message: string }> };
    const msg = gqlErr?.graphQLErrors?.[0]?.message || 'Could not update notification preference.';
    toast.add({ severity: 'error', summary: 'Error', detail: msg, life: 4000 });
  }
}

// Filters
const searchTerm = ref('');
const selectedLevel = ref<string | null>(null);
const currentPage = ref(1);
const perPage = 15;

const { levelOptions: tertiaryLevelOptions } = useTertiaryLevels();
const levelOptions = computed(() =>
  tertiaryLevelOptions.value.map(l => ({ label: l.classroomName, value: l.classroomName })),
);

const filterInput = computed(() => ({
  search: searchTerm.value || undefined,
  level: selectedLevel.value || undefined,
  page: currentPage.value,
  first: perPage,
}));

// Approval query
const {
  result: approvalResult,
  loading: approvalLoading,
  refetch: refetchApproval,
} = usePendingCourseRegistrationsForApprovalQuery(() => ({ filter: filterInput.value }),
  () => ({ enabled: activeView.value === 'approval', fetchPolicy: 'network-only' }),
);

// Endorsement query
const {
  result: endorsementResult,
  loading: endorsementLoading,
  refetch: refetchEndorsement,
} = usePendingCourseRegistrationsForEndorsementQuery(() => ({ filter: filterInput.value }),
  () => ({ enabled: activeView.value === 'endorsement', fetchPolicy: 'network-only' }),
);

const loading = computed(() =>
  activeView.value === 'approval' ? approvalLoading.value : endorsementLoading.value,
);

const approvalData = computed(() => approvalResult.value?.pendingCourseRegistrationsForApproval);
const endorsementData = computed(() => endorsementResult.value?.pendingCourseRegistrationsForEndorsement);

const registrations = computed(() => {
  const src = activeView.value === 'approval' ? approvalData.value : endorsementData.value;
  return src?.data ?? [];
});

const totalRecords = computed(() => {
  const src = activeView.value === 'approval' ? approvalData.value : endorsementData.value;
  return src?.paginatorInfo?.total ?? 0;
});

const totalPages = computed(() => {
  const src = activeView.value === 'approval' ? approvalData.value : endorsementData.value;
  return src?.paginatorInfo?.lastPage ?? 1;
});

const approvalCount = computed(() => approvalData.value?.paginatorInfo?.total ?? 0);
const endorsementCount = computed(() => endorsementData.value?.paginatorInfo?.total ?? 0);

// Reset page when switching view or filtering
watch(activeView, () => { currentPage.value = 1; });

function handleSearch() {
  currentPage.value = 1;
}

function onPageChange(event: { first: number }) {
  currentPage.value = Math.floor(event.first / perPage) + 1;
}

// Detail dialog
const showDetailDialog = ref(false);
const selectedRegistration = ref<ApprovalRegistration | null>(null);

const selectedRegistrationCourses = computed(() => {
  const regs = selectedRegistration.value?.registrations || [];
  return regs.flatMap((r: { coursesRegistered?: Array<Record<string, unknown>> }) => r.coursesRegistered || []);
});

function openDetailDialog(reg: ApprovalRegistration) {
  selectedRegistration.value = reg;
  showDetailDialog.value = true;
}

// Action dialog (approve / endorse)
const showActionDialog = ref(false);
const actionTarget = ref<ApprovalRegistration | null>(null);
const pendingAction = ref<'approve' | 'endorse'>('approve');
const mutating = ref(false);

function openActionDialog(reg: ApprovalRegistration, action: 'approve' | 'endorse') {
  actionTarget.value = reg;
  pendingAction.value = action;
  showActionDialog.value = true;
}

const { mutate: approveMutate } = useApproveStudentCourseRegistrationMutation();
const { mutate: endorseMutate } = useEndorseCourseRegistrationByHodMutation();
const { mutate: rejectMutate } = useRejectStudentCourseRegistrationMutation();

async function handleAction() {
  if (!actionTarget.value) return;
  mutating.value = true;
  const input = {
    student_id: actionTarget.value.student.id,
    academic_calendar_id: actionTarget.value.academic_calendar.id,
  };

  try {
    if (pendingAction.value === 'approve') {
      await approveMutate({ input });
      toast.add({ severity: 'success', summary: 'Approved', detail: 'Course registration approved successfully.', life: 3000 });
    } else {
      await endorseMutate({ input });
      toast.add({ severity: 'success', summary: 'Endorsed', detail: 'Course registration endorsed successfully.', life: 3000 });
    }
    showActionDialog.value = false;
    refetchCurrent();
  } catch (err: unknown) {
    const gqlErr = err as { graphQLErrors?: Array<{ message: string }> };
    const msg = gqlErr?.graphQLErrors?.[0]?.message || 'Action failed.';
    toast.add({ severity: 'error', summary: 'Error', detail: msg, life: 4000 });
  } finally {
    mutating.value = false;
  }
}

// Reject dialog
const showRejectDialog = ref(false);
const rejectTarget = ref<ApprovalRegistration | null>(null);
const rejectReason = ref('');

function openRejectDialog(reg: ApprovalRegistration) {
  rejectTarget.value = reg;
  rejectReason.value = '';
  showRejectDialog.value = true;
}

async function handleReject() {
  if (!rejectTarget.value || !rejectReason.value.trim()) return;
  mutating.value = true;

  try {
    await rejectMutate({
      input: {
        student_id: rejectTarget.value.student.id,
        academic_calendar_id: rejectTarget.value.academic_calendar.id,
        reason: rejectReason.value.trim(),
      },
    });
    toast.add({ severity: 'success', summary: 'Rejected', detail: 'Course registration rejected.', life: 3000 });
    showRejectDialog.value = false;
    refetchCurrent();
  } catch (err: unknown) {
    const gqlErr = err as { graphQLErrors?: Array<{ message: string }> };
    const msg = gqlErr?.graphQLErrors?.[0]?.message || 'Rejection failed.';
    toast.add({ severity: 'error', summary: 'Error', detail: msg, life: 4000 });
  } finally {
    mutating.value = false;
  }
}

function refetchCurrent() {
  if (activeView.value === 'approval') {
    refetchApproval();
  } else {
    refetchEndorsement();
  }
}

// Bulk actions (multi-select + apply-to-all-filtered)
const selectedRows = ref<ApprovalRegistration[]>([]);
const showBulkDialog = ref(false);
const bulkAction = ref<ApprovalActionEnum>(ApprovalActionEnum.Approve);
const bulkMode = ref<'selection' | 'scope'>('selection');
const bulkReason = ref('');
const bulkMutating = ref(false);

const scopeAvailable = computed(() => hasScopeCriteria({ level: selectedLevel.value }));

// Selection only makes sense for the rows currently on screen.
watch([activeView, currentPage], () => {
  selectedRows.value = [];
});

const { mutate: bulkMutate } = useMutation<{ bulkCourseRegistrationAction: BulkRegistrationActionResult }>(
  BULK_COURSE_REGISTRATION_ACTION,
);

const bulkConfirmMessage = computed(() => {
  const verb = bulkActionLabel(bulkAction.value).toLowerCase();
  if (bulkMode.value === 'selection') {
    return `This will ${verb} the course registrations of ${selectedRows.value.length} selected student(s).`;
  }
  const scope = selectedLevel.value ? `Level ${selectedLevel.value}` : 'the current filters';
  const eligible = activeView.value === 'approval' ? 'pending' : 'adviser-approved';
  return `This will ${verb} ALL ${eligible} registrations matching ${scope} in the active calendar. The search box is not applied.`;
});

function openBulkDialog(action: ApprovalActionEnum, mode: 'selection' | 'scope') {
  bulkAction.value = action;
  bulkMode.value = mode;
  bulkReason.value = '';
  showBulkDialog.value = true;
}

async function handleBulkAction() {
  if (bulkAction.value === ApprovalActionEnum.Reject && !bulkReason.value.trim()) return;
  if (bulkMode.value === 'selection' && !selectedRows.value.length) return;
  bulkMutating.value = true;

  // Each table row is a per-student AGGREGATE; the backend's bulk mutation
  // acts on the individual student_course_registrations ids inside it —
  // never on student ids (those live in a different id space).
  const input = bulkMode.value === 'selection'
    ? buildSelectionBulkInput(
      bulkAction.value,
      registrationIdsFromRows(selectedRows.value),
      bulkReason.value,
    )
    : buildScopeBulkInput(bulkAction.value, { level: selectedLevel.value }, bulkReason.value);

  try {
    const response = await bulkMutate({ input });
    const result = response?.data?.bulkCourseRegistrationAction;
    toast.add({
      severity: result && result.affected_count > 0 ? 'success' : 'warn',
      summary: 'Bulk Action',
      detail: result?.message ?? 'Bulk action completed.',
      life: 4000,
    });
    showBulkDialog.value = false;
    selectedRows.value = [];
    refetchCurrent();
  } catch (err: unknown) {
    const gqlErr = err as { graphQLErrors?: Array<{ message: string }> };
    const msg = gqlErr?.graphQLErrors?.[0]?.message || 'Bulk action failed.';
    toast.add({ severity: 'error', summary: 'Error', detail: msg, life: 4000 });
  } finally {
    bulkMutating.value = false;
  }
}

// course_status is the curriculum category (C = Compulsory, E = Elective), not an approval state.
function courseStatusLabel(status?: string): string {
  switch ((status || '').toUpperCase()) {
  case 'C': return 'Compulsory';
  case 'E': return 'Elective';
  default: return status || '—';
  }
}

function courseStatusSeverity(status?: string): 'success' | 'secondary' {
  return (status || '').toUpperCase() === 'C' ? 'success' : 'secondary';
}

function formatDate(d: string) {
  try {
    return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return d;
  }
}
</script>
